'use client'

import React, { useMemo } from 'react'
import { $DCprimaryColor, $DCWarningColor, DcChart, colors } from 'pixel-base'
import { formatCurrency } from './chartStyles'
import { colorSquare } from './chartStyles'
import {
  baseChartStyles,
  createYAxisConfig,
  applySeriesStyles,
} from './chartStyles'
import { useDashboardData, DashboardFilters } from './useDashboardData'

interface ChartParam {
  axisValue: string
  value: number
  color: string
  seriesName: string
}

interface ChartSeries {
  name: string
  type: string
  stack?: string
  yAxisIndex?: number
  showSymbol: boolean
  symbol: string
  symbolSize: number
  data: number[]
  itemStyle: {
    color: string
  }
  z: number
  emphasis?: {
    itemStyle: {
      symbolSize: number
    }
  }
}

interface RevenueChartProps {
  filters: DashboardFilters
  height?: string | number
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
}

const RevenueChart: React.FC<RevenueChartProps> = ({
  filters,
  height = 350,
  setComponent,
  setShowDetail,
}: RevenueChartProps) => {
  const { data, loading, xAxisDates, totalRevenue } = useDashboardData(
    filters,
    'revenue',
  )

  // Calculate average order value from the data
  const avgOrderValue = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return 0
    const totalOrders = data.reduce(
      (sum, item) => sum + (item.total_orders || 0),
      0,
    )
    return totalOrders > 0 ? totalRevenue / totalOrders : 0
  }, [data, totalRevenue])

  const formattedData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        xAxis: [],
        dualYAxis: false,
        yAxisNames: ['Revenue'],
        series: [],
      }
    }

    const revenueData = data.map((item) => item.total_revenue || 0)
    const avgOrderValueData = data.map((item) => item.avg_order_value || 0)

    return {
      xAxis: xAxisDates,
      dualYAxis: false,
      showSymbol: false,
      yAxisNames: ['Revenue'],
      series: [
        {
          name: 'Total Revenue',
          type: 'line',
          showSymbol: false,
          symbol: 'none',
          symbolSize: 0,
          data: revenueData,
          yAxisIndex: 0,
          itemStyle: { color: $DCprimaryColor },
          z: 3,
        },
        {
          name: 'Avg Order Value',
          type: 'line',
          showSymbol: false,
          symbol: 'none',
          symbolSize: 0,
          data: avgOrderValueData,
          yAxisIndex: 0,
          itemStyle: { color: $DCWarningColor },
          z: 2,
        },
      ],
    }
  }, [data, xAxisDates])

  const chartOptions = {
    yAxis: createYAxisConfig({ name: 'Revenue', color: $DCprimaryColor }),
    tooltip: {
      ...baseChartStyles.tooltip,
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#5f38f9',
          width: 2,
          type: 'dashed',
        },
      },
      formatter: (params: ChartParam[]) => {
        if (!params || params.length === 0) return ''

        const date = params[0].axisValue
        let html = `
          <div style="padding: 12px; background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.1); border: 1px solid rgba(0,0,0,0.05);">
            <div style="text-align: center; margin-bottom: 12px;">
              <div style="font-size: 16px; font-weight: 700; color: #2c3e50; margin-bottom: 4px;">${date}</div>
              <div style="height: 2px; background: linear-gradient(90deg, #5f38f9 0%, #7c3aed 100%); margin: 0 auto; width: 60px; border-radius: 1px;"></div>
            </div>
            <div style="display: flex; flex-direction: column; gap: 8px;">
        `

        params.forEach((param: ChartParam) => {
          const value = param.value || 0
          const color = param.color
          const seriesName = param.seriesName

          // Format values appropriately
          const formattedValue =
            seriesName === 'Total Revenue'
              ? formatCurrency(value)
              : `PKR ${value.toFixed(2)}`

          html += `
            <div style="display: flex; justify-content: space-between; align-items: center; padding: 8px; background: rgba(255,255,255,0.8); border-radius: 6px; border: 1px solid rgba(0,0,0,0.05);">
              <div style="display: flex; align-items: center; gap: 8px;">
                <div style="width: 12px; height: 12px; background: ${color}; border-radius: 3px;"></div>
                <span style="font-size: 12px; font-weight: 600; color: #495057;">${seriesName}</span>
              </div>
              <div style="font-size: 14px; font-weight: 700; color: #2c3e50; margin-left: 20px;">${formattedValue}</div>
            </div>
          `
        })

        html += `
            </div>
          </div>
        `

        return html
      },
    },
  }

  return (
    <DcChart
      type="mixedLineBar"
      title="Revenue"
      isLoading={loading || !data || data.length === 0}
      headerData={{
        title: 'Revenue Summary',
        data: `Total: ${formatCurrency(
          totalRevenue,
        )} | Avg Order: ${formatCurrency(avgOrderValue)}`,
      }}
      isTitleClickable={true}
      onClickTitle={() => {
        setComponent('revenueChart')
        setShowDetail(true)
      }}
      data={formattedData}
      height={height}
      options={{
        yAxis: chartOptions.yAxis,
        tooltip: chartOptions.tooltip,
        series: applySeriesStyles(formattedData.series, baseChartStyles, [
          $DCprimaryColor,
          $DCWarningColor,
        ]),
        // Ensure smooth properties are explicitly set
        smooth: true,
        smoothMonotone: 'x',
      }}
    />
  )
}

export default RevenueChart
